import type {Icon} from '@primer/octicons-react'
import {AlertIcon, CheckCircleFillIcon, GitMergeIcon, GitPullRequestIcon} from '@primer/octicons-react'
import {Label, type LabelColorOptions} from '@primer/react'

import type {
  StackSectionEntryType,
  StackSectionEntryStatus,
} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import type {StackRebasePullRequestStatus} from '../../../hooks/mutations/use-stack-rebase-mutation'

export type EntryIconInfo = {type: 'icon'; icon: Icon; className: string; ariaLabel?: string} | {type: 'spinner'}

export type EntryStatusInfo = {
  label: {variant: LabelColorOptions; text: string} | null
  icon: EntryIconInfo
}

export const ENTRY_STATUS_INFO: Record<StackSectionEntryStatus, EntryStatusInfo> = {
  IRRELEVANT_MERGED: {
    label: {variant: 'done', text: 'Merged'},
    icon: {type: 'icon', icon: GitMergeIcon, className: 'color-fg-done'},
  },
  IRRELEVANT_AFTER: {
    label: null,
    icon: {
      type: 'icon',
      icon: GitPullRequestIcon,
      className: 'color-fg-muted',
      ariaLabel: 'Will not merge: higher up the stack',
    },
  },
  BLOCKED_STACK: {
    label: {variant: 'attention', text: 'Needs rebase'},
    icon: {type: 'icon', icon: AlertIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  OK: {
    label: {variant: 'success', text: 'Ready to merge'},
    icon: {type: 'icon', icon: CheckCircleFillIcon, className: 'color-fg-success'},
  },
  LOADING_MERGE_STATE: {
    label: null,
    icon: {type: 'spinner'},
  },
  ERROR_MERGE_STATE: {
    label: {variant: 'attention', text: 'Error loading data'},
    icon: {
      type: 'icon',
      icon: AlertIcon,
      className: 'color-fg-attention',
      ariaLabel: 'Cannot load mergeability data',
    },
  },
  BLOCKED_MERGE_STATE: {
    label: {variant: 'attention', text: 'Failing rules'},
    icon: {type: 'icon', icon: AlertIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  UNKNOWN_MERGE_STATE: {
    label: {variant: 'default', text: 'Pending'},
    icon: {type: 'spinner'},
  },
}

export function EntryStatusLabel({
  entry,
  rebaseState,
  allBelowCanMerge,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
  allBelowCanMerge?: boolean
}) {
  // Rebasing
  if (rebaseState === 'SUCCEEDED') {
    return <Label variant="success">Rebased</Label>
  } else if (rebaseState === 'CONFLICTED') {
    return <Label variant="danger">Merge conflicts</Label>
  } else if (rebaseState === 'FAILED') {
    return <Label variant="danger">Rebase failed</Label>
  } else if (rebaseState === 'PENDING') {
    return <Label variant="default">Rebasing…</Label>
  }

  if (entry.status === 'OK' && entry.position === 'CURRENT') {
    return <Label variant="success">Meets stack requirements</Label>
  }

  if (entry.status === 'OK' && entry.position === 'BEFORE') {
    return <Label variant="success">{allBelowCanMerge ? 'Able to merge' : 'Ready to merge'}</Label>
  }

  const {label} = ENTRY_STATUS_INFO[entry.status]
  if (!label) return null
  return <Label variant={label.variant}>{label.text}</Label>
}
